import React from "react";
import clsx from "clsx";
import { Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Typography from "./Typography";
import { getCookie } from "utils/utils";

type ButtonProps = {
  id?: string;
  type?:
    | "Primary"
    | "Default"
    | "Dashed"
    | "Text"
    | "Link"
    | "Disabled"
    | "Danger";
  size?: "Small" | "Medium" | "Large";
  icon?: boolean;
  iconComponent?: React.ReactNode;
  buttonText?: string;
  onClick?: () => void;
  theme?: "light" | "dark";
  className?: string;
  locked?: boolean;
  lockedMessage?: string;
  loading?: boolean;
};

const typeClasses = {
  light: {
    Primary: "bg-[#0F428D] text-white",
    Default: "bg-white border border-[#0F428D] text-[#0F428D]",
    Dashed: "border-dashed bg-white border border-[#0F428D] text-[#0F428D]",
    Text: "bg-transparent text-blue-primary",
    Link: "text-blue-primary underline",
    Disabled:
      "border-[1px] border-[#D9D9D9] text-black/60 cursor-not-allowed bg-white",
    Danger: "text-[#FF4D4F] hover:bg-[#FF4D4F] hover:text-white border border-red-500",
  },
  dark: {
    Primary: "bg-[#7EADF2] text-white",
    Default: "border border-[#7EADF2] text-[#7EADF2]",
    Dashed: "border-dashed border border-[#7EADF2] text-[#7EADF2]",
    Text: "bg-transparent text-blue-400",
    Link: "text-blue-400 underline",
    Disabled:
      "border-[1px] border-[#D9D9D9] text-black cursor-not-allowed bg-white",
    Danger: "text-[#FF4D4F] hover:bg-[#FF4D4F] hover:text-white",
  },
};

const sizeClasses = {
  Small: "text-sm px-2 py-1 rounded-md",
  Medium: "text-md px-4 py-1 rounded-lg",
  Large: "text-lg px-6 py-1 rounded-lg",
};

const Button: React.FC<ButtonProps> = ({
  id,
  type = "Default",
  size = "Medium",
  icon = false,
  iconComponent,
  buttonText,
  onClick,
  theme = "light",
  className = "",
  locked = false,
  lockedMessage = "Upgrade to Professional Plan",
  loading = false,
}) => {
  const isDisabled = type === "Disabled" || loading;

  const isAuthorised = Boolean(getCookie("token"));

  const buttonContent = (
    <button
      id={id}
      className={clsx(
        `relative flex items-center justify-center min-h-8 transition px-4 py-1`,
        typeClasses[theme][type],
        sizeClasses[size],
        className,
        isDisabled && "cursor-not-allowed opacity-40",
        locked && "cursor-default"
      )}
      onClick={!isDisabled ? onClick : undefined}
      disabled={isDisabled}
    >
      <div className="relative flex items-center justify-center gap-2">
        {loading ? (
          <LoadingOutlined className="animate-spin text-base" />
        ) : (
          icon && iconComponent && <span>{iconComponent}</span>
        )}
        {buttonText && <Typography variant="Base" text={buttonText} />}
      </div>
    </button>
  );

  return locked ? (
    <Tooltip title={isAuthorised ? lockedMessage : "Signup Required"} placement="top">
      <div className="inline-block">{buttonContent}</div>
    </Tooltip>
  ) : (
    buttonContent
  );
};

export default Button;
